import type { MapManager } from '@onxmaps/map-x'
import {
  INIT_LAYER_MANAGEMENT_START,
  INIT_LAYER_MANAGEMENT_STOP,
  INIT_LAYER_MANAGEMENT_TIME,
  INIT_STYLESHEET_START,
  INIT_STYLESHEET_STOP,
  INIT_STYLESHEET_TIME,
  TIME_TO_MAP_IDLE_ON_PAGE_LOAD,
  TIME_TO_MAP_IDLE_ON_PAGE_LOAD_STOP,
  TIME_TO_MAP_LOAD_ON_PAGE_LOAD,
  TIME_TO_MAP_LOAD_ON_PAGE_LOAD_STOP,
  TIME_TO_MAP_STYLE_LOAD_ON_PAGE_LOAD,
  TIME_TO_MAP_STYLE_LOAD_ON_PAGE_LOAD_STOP,
} from '@/lib/performance-marks'
import type { NetworkInformation } from '@/types/network-information'

type Emit = (event: 'map-event', ...args: unknown[]) => void

let appTimeToMapIdleOnPageLoad: number | null = null
let appTimeToMapLoadOnPageLoad: number | null = null
let appTimeToMapStyleLoadOnPageLoad: number | null = null
let timeForStylesheetInit: number | null = null

// Performance metric getters
/**
 * *(AI-generated)*
 * Returns the recorded time-to-map-idle performance metric in milliseconds, or null if not yet measured.
 */
export const getTimeToMapIdle = () => appTimeToMapIdleOnPageLoad
/**
 * *(AI-generated)*
 * Returns the recorded time-to-map-load performance metric in milliseconds, or null if not yet measured.
 */
export const getTimeToMapLoad = () => appTimeToMapLoadOnPageLoad
/**
 * *(AI-generated)*
 * Returns the recorded time-to-map-style-load performance metric in milliseconds, or null if not yet measured.
 */
export const getTimeToMapStyleLoad = () => appTimeToMapStyleLoadOnPageLoad
/**
 * *(AI-generated)*
 * Returns the recorded time for stylesheet initialization in milliseconds, or null if not yet measured.
 */
export const getTimeForStyleInit = () => timeForStylesheetInit

/**
 * Log performance event to Firebase with connection info and href
 * @param eventName name of event to log to firebase performance
 * @param time_ms time in milliseconds
 * @param emit function to emit events
 */
function logPerformanceEvent(eventName: string, time_ms: number, emit: Emit) {
  // Only track in prod
  if (location.origin === 'https://fish.onxmaps.com') {
    const connection: NetworkInformation | undefined =
      navigator.connection ||
      // @ts-expect-error experimental vendor types
      navigator.mozConnection ||
      // @ts-expect-error experimental vendor types
      navigator.webkitConnection
    const connection_type = connection?.effectiveType ?? 'n/a'
    const connection_downlink = connection?.downlink ?? 'n/a'

    emit('map-event', {
      name: eventName,
      type: 'performance',
      time_ms,
      href: location.href,
      connection_type,
      connection_downlink,
    })
  }
}

/**
 * Time for the stylesheet to be loaded and mutated on the client
 * @param emit function to emit events
 */
function measureStyleInitTime(emit: Emit) {
  const measured = performance.measure(
    INIT_STYLESHEET_TIME,
    INIT_STYLESHEET_START,
    INIT_STYLESHEET_STOP
  )

  timeForStylesheetInit = measured.duration
  logPerformanceEvent(INIT_STYLESHEET_TIME, timeForStylesheetInit, emit)
}

/**
 * Time for layer management library to initialize.
 * @param emit function to emit events
 */
function measureLayerManagementTime(emit: Emit) {
  const measured = performance.measure(
    INIT_LAYER_MANAGEMENT_TIME,
    INIT_LAYER_MANAGEMENT_START,
    INIT_LAYER_MANAGEMENT_STOP
  )

  logPerformanceEvent(INIT_LAYER_MANAGEMENT_TIME, measured.duration, emit)
}

/**
 * The time from app load to the first time map is idle
 * Meaning the map is fully drawn and loaded
 * A more accurate metric than LCP for us
 * @param emit function to emit events
 */
function measureMapTimeToIdle(emit: Emit) {
  // Log performance metric of time since app start of first time map is
  // fully loaded and rendered to screen
  performance.mark(TIME_TO_MAP_IDLE_ON_PAGE_LOAD_STOP)

  const measured = performance.measure(
    TIME_TO_MAP_IDLE_ON_PAGE_LOAD,
    undefined,
    TIME_TO_MAP_IDLE_ON_PAGE_LOAD_STOP
  )

  appTimeToMapIdleOnPageLoad = measured.duration
  logPerformanceEvent(
    TIME_TO_MAP_IDLE_ON_PAGE_LOAD,
    appTimeToMapIdleOnPageLoad,
    emit
  )
}

/**
 * Time to the first mapbox load event used by the loader bar and some of our
 * e2e tests to know when to start
 * @param emit function to emit events
 */
function measureMapLoadTime(emit: Emit) {
  // Measure time from start of session to map load event
  performance.mark(TIME_TO_MAP_LOAD_ON_PAGE_LOAD_STOP)
  const measured = performance.measure(
    TIME_TO_MAP_LOAD_ON_PAGE_LOAD,
    undefined,
    TIME_TO_MAP_LOAD_ON_PAGE_LOAD_STOP
  )

  appTimeToMapLoadOnPageLoad = measured.duration
  logPerformanceEvent(
    TIME_TO_MAP_LOAD_ON_PAGE_LOAD,
    appTimeToMapLoadOnPageLoad,
    emit
  )
}

/**
 * Time for stylesheet to load by the map on page load
 * @param emit function to emit events
 */
function measureMapStyleDateLoadTime(emit: Emit) {
  performance.mark(TIME_TO_MAP_STYLE_LOAD_ON_PAGE_LOAD_STOP)
  const measured = performance.measure(
    TIME_TO_MAP_STYLE_LOAD_ON_PAGE_LOAD,
    undefined,
    TIME_TO_MAP_STYLE_LOAD_ON_PAGE_LOAD_STOP
  )

  appTimeToMapStyleLoadOnPageLoad = measured.duration
  logPerformanceEvent(
    TIME_TO_MAP_STYLE_LOAD_ON_PAGE_LOAD,
    appTimeToMapStyleLoadOnPageLoad,
    emit
  )
}

/**
 * Setup listeners for mapbox events to measure perf metrics
 * @param mapManager a MapManager instance
 * @param emit function to emit events
 */
export const initMapTimingPerformanceMetrics = (
  mapManager: MapManager,
  emit: Emit
) => {
  mapManager.events.once('map-x-idle', () => {
    measureMapTimeToIdle(emit)
  })

  mapManager.events.once('map-x-load', () => {
    measureMapLoadTime(emit)
  })

  mapManager.events.once('map-x-style-data', () => {
    measureStyleInitTime(emit)
    measureLayerManagementTime(emit)
    measureMapStyleDateLoadTime(emit)
  })
}
